import React from 'react'
import PropTypes from 'prop-types'
import { useFilterValue, useWillConstrain } from '@morningtrain/react-filters'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import moment from 'moment'
import ReactDOM from 'react-dom'
import PopperElement from 'services/PopperElement'
import { FieldWrapper } from '@morningtrain/react-fields'
import OnClickOutside from 'services/OnClickOutside'
import * as UI from 'ui'
import da from 'date-fns/locale/da'
import { Translation } from '@morningtrain/react-translations'

registerLocale('da', da)

function PeriodFilter (props) {
  const referenceElementRef = React.useRef(null)

  const [value, setValue] = useFilterValue(props.name, props.defaultValue)

  const defaultFromValue = value && value.from ? moment(value.from).toDate() : null
  const defaultToValue = value && value.to ? moment(value.to).toDate() : null

  const [from, setFrom] = React.useState(defaultFromValue)
  const [to, setTo] = React.useState(defaultToValue)

  const [active, setActive] = React.useState(false)

  const shouldConstrainCheck = React.useCallback((val) => {
    return !!val && ((val.from && val.to) || (val.from === null && val.to === null))
  }, [])

  useWillConstrain(props.name, value, shouldConstrainCheck)

  React.useEffect(() => {
    const nextFrom = from !== null ? moment(from).format('YYYY-MM-DD') : null
    const nextTo = to !== null ? moment(to).format('YYYY-MM-DD') : null

    if ((nextFrom === null && nextTo === null) === false && (nextFrom === null || nextTo === null)) {
      return
    }

    if (value === null || nextFrom !== value.from || nextTo !== value.to) {
      setValue({
        from: nextFrom,
        to: nextTo
      })
      setActive(false)
    }
  }, [from, to])

  React.useEffect(() => {
    if (typeof props.onChange === 'function') {
      props.onChange(value)
    }
  }, [value])

  const clearPeriod = (e) => {
    setFrom(null)
    setTo(null)
    e.stopPropagation()
  }

  const handleSelect = (toSelect) => {
    let _from = null
    let _to = null

    if (toSelect === 'yesterday') {
      setFrom(moment().subtract(1, 'day').toDate())
      setTo(moment().subtract(1, 'day').toDate())
    }
    if (toSelect === 'today') {
      setFrom(moment().toDate())
      setTo(moment().toDate())
    }
    if (toSelect === 'this_week') {
      setFrom(moment().startOf('isoWeek').toDate())
      setTo(moment().endOf('isoWeek').toDate())
    }
    if (toSelect === 'this_month') {
      setFrom(moment().startOf('month').toDate())
      setTo(moment().endOf('month').toDate())
    }
    if (toSelect === 'last_year') {
      _from = moment().startOf('year').subtract(1, 'year')
      _to = _from.clone().endOf('year')
    }
    if (toSelect === 'latest_year') {
      _from = moment().subtract(1, 'year')
      _to = moment()
    }
    if (toSelect === 'latest_year_full_months') {
      _from = moment().startOf('month').subtract(1, 'year')
      _to = moment().subtract(1, 'month').endOf('month')
    }
    if (toSelect === 'this_year') {
      setFrom(moment().startOf('year').toDate())
      setTo(moment().toDate())
    }

    if (_from !== null) {
      setFrom(_from.toDate())
    }

    if (_to !== null) {
      setTo(_to.toDate())
    }
  }

  const isActive = (period) => {
    let _from = null
    let _to = null

    if (period === 'yesterday') {
      return moment().subtract(1, 'day').isSame(from, 'day') && moment().subtract(1, 'day').isSame(to, 'day')
    }

    if (period === 'today') {
      return moment().isSame(from, 'day') && moment().isSame(to, 'day')
    }

    if (period === 'this_week') {
      return moment().startOf('isoWeek').isSame(from, 'day') && moment().endOf('isoWeek').isSame(to, 'day')
    }

    if (period === 'this_month') {
      return moment().startOf('month').isSame(from, 'day') && moment().endOf('month').isSame(to, 'day')
    }

    if (period === 'last_year') {
      _from = moment().startOf('year').subtract(1, 'year')
      _to = _from.clone().endOf('year')
    }

    if (period === 'latest_year') {
      _from = moment().subtract(1, 'year')
      _to = moment()
    }

    if (period === 'latest_year_full_months') {
      _from = moment().startOf('month').subtract(1, 'year')
      _to = moment().subtract(1, 'month').endOf('month')
    }

    if (period === 'this_year') {
      return moment().startOf('year').isSame(from, 'day') && moment().isSame(to, 'day')
    }

    if (_from !== null && _to !== null) {
      return _from.isSame(from, 'day') && _to.isSame(to, 'day')
    }

    return false
  }

  const defaultSelectedSinceOpen = { from: null, to: null }
  const [selectedSinceOpen, setSelectedSinceOpen] = React.useState(defaultSelectedSinceOpen)

  React.useEffect(() => {
    setSelectedSinceOpen(defaultSelectedSinceOpen)
  }, [active])

  const onToChanged = (date) => {
    setTo(date)

    setSelectedSinceOpen({
      ...selectedSinceOpen,
      to: date
    })

    if (props.shouldCloseOnSelect && selectedSinceOpen.from !== null) {
      setActive(false)
    }
  }

  const onFromChanged = (date) => {
    setFrom(date)

    setSelectedSinceOpen({
      ...selectedSinceOpen,
      from: date
    })

    if (props.shouldCloseOnSelect && selectedSinceOpen.to !== null) {
      setActive(false)
    }
  }

  const Selector = ({ name, transName }) => {
    if (Array.isArray(props.selectors) && !props.selectors.includes(name)) {
      return null
    }

    return (
      <UI.Text
        color={isActive(name) ? 'primary' : null}
        className='px-2 whitespace-nowrap cursor-pointer text-sm font-normal text-gray-400 hover:text-primary-400' align='center' block
        onClick={() => handleSelect(name)}
      >
        <Translation name={'generic.' + (transName || name)} />
      </UI.Text>
    )
  }

  return (
    <FieldWrapper label={props.label} className={props.className}>
      <div className='datepicker-container flex items-center'>
        {props.withSelectors
          ? (
            <UI.ButtonWrap className="mr-6" innerStyle={{ width: '400px' }} grow wrap={false}>
              <Selector name="yesterday"/>
              <Selector name="today"/>
              <Selector name="this_week"/>
              <Selector name="this_month"/>
              <Selector name="this_year"/>
              <Selector name="last_year"/>
              <Selector name="latest_year"/>
              <Selector name="latest_year_full_months" transName="latest_year"/>
            </UI.ButtonWrap>
          )
          : null}
        <div ref={referenceElementRef} onClick={() => setActive(!active)} className='datepicker-selector'>
          {(from !== null || to !== null) && props.clearable ? (
            <button className='pr-3' onClick={(e) => clearPeriod(e)}>
              <UI.Icons.Decline />
            </button>) : null}
          {from !== null ? moment(from).format('DD-MM-YYYY') : null} - {to !== null ? moment(to).format('DD-MM-YYYY') : null}
        </div>
        {!active ? null : ReactDOM.createPortal((
          <PopperElement referenceElementRef={referenceElementRef.current}>
            <OnClickOutside onClickOutside={() => setActive(false)}>
              <div className='flex space-x-6 p-4 bg-white shadow-xl border rounded'>
                <DatePicker
                  selected={from}
                  startDate={from}
                  endDate={to}
                  onChange={(dates) => {
                    const [start, end] = dates

                    onFromChanged(start)
                    onToChanged(end)
                  }}
                  showWeekNumbers
                  formatWeekDay={nameOfDay => nameOfDay.substr(0, 3)}
                  peekNextMonth
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode='select'
                  locale='da'
                  selectsRange
                  inline
                />
              </div>
            </OnClickOutside>
          </PopperElement>
        ), document.body)}
      </div>
    </FieldWrapper>
  )
}

PeriodFilter.defaultProps = {
  defaultValue: { from: null, to: null },
  clearable: false,
  shouldCloseOnSelect: false,
  selectors: ['today', 'this_week', 'this_month', 'this_year']
}

PeriodFilter.propTypes = {
  name: PropTypes.string.isRequired,
  clearable: PropTypes.bool,
  shouldCloseOnSelect: PropTypes.bool,
  selectors: PropTypes.array
}

export default PeriodFilter
